import type { Database, SupabaseClient } from "@repo/supabase/database-types"
import { logger } from "@repo/utils/logger"
import type {
  ColumnFilter,
  ColumnFiltersQueryParam,
  DataTableBaseSearch,
} from "~/lib/schemas/common"
import { getSearchQuery } from "~/lib/utils/search-query"

type Schema = Database["public"]
type TableName = keyof Schema["Tables"]
type ViewName = keyof Schema["Views"]
type TableOrView = TableName | ViewName

type TableOrViewRow<T extends TableOrView> = T extends TableName
  ? Schema["Tables"][T]["Row"]
  : T extends ViewName
    ? Schema["Views"][T]["Row"]
    : never

type GenericFilterBuilder = ReturnType<
  ReturnType<SupabaseClient["from"]>["select"]
>

export type FilterHandler<_T extends TableOrView> = (
  query: GenericFilterBuilder,
  value: unknown,
) => GenericFilterBuilder

export interface QueryConfig<T extends TableOrView> {
  /**
   * @description The name of the table or view to query.
   */
  table: T
  /**
   * @description The columns to select from the table.
   * @default "*"
   */
  select?: string
  /**
   * @description The field to use for full-text search.
   */
  searchField?: string
  /**
   * @description The strategy to use for the count.
   * @default "exact"
   */
  countStrategy?: "exact" | "estimated" | "planned"
  /**
   * @description The default sort order.
   */
  defaultSort?: { field: keyof TableOrViewRow<T>; ascending: boolean }
  /**
   * @description A mapping of frontend column names to database column names.
   */
  fieldMappings?: Record<string, keyof TableOrViewRow<T>>
  /**
   * @description The filters to apply to the query.
   */
  filters: Record<string, FilterHandler<T>>
}

/**
 * @description A class that builds a query for a DataTable.
 * @template TableName - The name of the table or view to query.
 * @param supabase - The Supabase client.
 * @param config - The configuration for the query.
 * @returns A promise that resolves to the data and count of the query.
 *
 * @example
 * const builder = new DataTableQueryBuilder(supabase, {
 *   table: "users",
 *   select: "*",
 *   searchField: "name",
 *   defaultSort: { field: "name", ascending: true },
 *   fieldMappings: { id: "id" },
 *   filters: {
 *     name: createStringFilter("name"),
 *     email: createStringFilter("email"),
 *     status: createInPredefinedListFilter("status", ["active", "inactive"]),
 *     business_entity_unit_ids: createListFilter("business_entity_unit_id"),
 *     client_unit_ids: createListContainsFilter("client_unit_ids"),
 *   },
 * })
 *
 * const { data, count } = await builder.execute({
 *   sorting: [{ id: "name", desc: false }],
 *   page: 1,
 *   page_size: 10,
 *   columnFilters: [{ id: "name", value: "John" }],
 *   q: "John",
 * })
 */
export class DataTableQueryBuilder<T extends TableOrView> {
  constructor(
    private supabase: SupabaseClient,
    private config: QueryConfig<T>,
  ) {}

  async execute<TResult = TableOrViewRow<T>>(
    filters: DataTableBaseSearch,
  ): Promise<{
    data: TResult[]
    count: number
  }> {
    let query = this.supabase
      .from(this.config.table as TableName)
      .select(this.config.select ?? "*", {
        count: this.config.countStrategy ?? "exact",
      })

    // Apply sorting with type safety
    if (filters.sorting?.length) {
      filters.sorting.forEach((sort) => {
        const field = this.config.fieldMappings?.[sort.id] || sort.id

        if (typeof field === "string") {
          query = query.order(field, { ascending: !sort.desc })
        }
      })
    } else if (
      this.config.defaultSort &&
      typeof this.config.defaultSort.field === "string"
    ) {
      query = query.order(this.config.defaultSort.field, {
        ascending: this.config.defaultSort.ascending,
      })
    }

    // Apply pagination
    const page = filters.page ?? 1
    const pageSize = filters.page_size ?? 20
    query = query.range((page - 1) * pageSize, page * pageSize - 1)

    type QueryBuilder = typeof query

    // Apply column filters
    filters.columnFilters?.forEach((filter) => {
      const handler = this.config.filters[filter.id]
      if (handler) {
        query = handler(
          query as unknown as GenericFilterBuilder,
          filter.value,
        ) as unknown as QueryBuilder
      } else {
        logger.warn(`No filter handler found for column ${filter.id}`, {
          column: filter.id,
          value: filter.value,
        })
      }
    })

    // Apply text search
    const searchQuery = getSearchQuery(filters.q)
    if (searchQuery && this.config.searchField) {
      // Both function-based and column-based FTS use the same Supabase textSearch method
      // The difference is in the field name: function name vs column name
      query = query.textSearch(this.config.searchField, searchQuery, {
        type: "websearch",
        config: "english",
      })
    }

    const { data, error, count } = await query

    if (error) {
      throw new Error(`Failed to query ${this.config.table}: ${error.message}`)
    }

    return { data: (data || []) as TResult[], count: count ?? 0 }
  }
}

export const createStringFilter =
  <T extends TableOrView>(column: string): FilterHandler<T> =>
  (query: GenericFilterBuilder, value: unknown) => {
    if (typeof value === "string") {
      return query.eq(column, value)
    }
    return query
  }

export const createILikeFilter =
  <T extends TableOrView>(column: string): FilterHandler<T> =>
  (query: GenericFilterBuilder, value: unknown) => {
    if (typeof value === "string") {
      return query.ilike(column, `%${value}%`)
    }
    return query
  }

export const createInPredefinedListFilter =
  <V extends string, T extends TableOrView>(
    column: string,
    validValues: readonly V[],
  ): FilterHandler<T> =>
  (query: GenericFilterBuilder, value: unknown) => {
    if (Array.isArray(value)) {
      const values = value.filter((v) => validValues.includes(v as V))
      if (values.length > 0) {
        return query.in(column, values)
      }
    } else if (typeof value === "string" && validValues.includes(value as V)) {
      return query.eq(column, value)
    }
    return query
  }

export const createListFilter =
  <T extends TableOrView>(column: string): FilterHandler<T> =>
  (query: GenericFilterBuilder, value: unknown) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        return query.in(column, value)
      }
    } else if (value !== undefined && value !== null) {
      return query.in(column, [value])
    }
    return query
  }

export const createListContainsFilter =
  <T extends TableOrView>(column: string): FilterHandler<T> =>
  (query: GenericFilterBuilder, value: unknown) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        return query.contains(column, value)
      }
    } else if (value !== undefined && value !== null) {
      return query.contains(column, [value])
    }
    return query
  }

export const createBooleanFilter =
  <T extends TableOrView>(column: string): FilterHandler<T> =>
  (query: GenericFilterBuilder, value: unknown) => {
    if (typeof value === "boolean") {
      return query.eq(column, value)
    }
    return query
  }

export function ensureColumnFilter(
  filters: ColumnFiltersQueryParam,
  filter: ColumnFilter,
) {
  return (filters || []).filter((f) => f.id !== filter.id).concat(filter)
}
